export const MINIMUM_SECONDS_THRESHOLD = 1 * 60
export const MINIMUM_PAGE_PERCENTAGE_THRESHOLD = 80

/**
 * Returns both pages of the wowbook spread for a given reported page number.
 * Wowbook spreads: page 1 is solo, then [2,3], [4,5], … last page is solo.
 * - Even page n  → left side of spread: shows [n, n+1]
 * - Odd page n>1 → right side of spread: shows [n-1, n]
 */
export function getSpreadPages(pageNumber: number, totalPages: number): number[] {
  if (totalPages <= 1 || pageNumber <= 1) return [Math.max(1, pageNumber)]
  if (pageNumber >= totalPages) return [totalPages]
  if (pageNumber % 2 === 0) return [pageNumber, Math.min(pageNumber + 1, totalPages)]
  return [pageNumber - 1, pageNumber]
}

/**
 * Determines if the child has completed the reading.
 * Expects `pageNumbers` to already contain both pages of each spread
 * (via `getSpreadPages`), so no pattern inference is needed.
 * When given raw server-recorded page numbers (one per spread), the inference
 * here fills in the missing spread partner pages via adjacency and gap logic.
 */
export function isReadingComplete(totalPage: number, pageNumbers: number[]): boolean {
  if (totalPage <= 1) return pageNumbers.includes(1)

  const uniquePages = new Set(pageNumbers)
  const inferredPages = new Set<number>()

  if (pageNumbers.length > 0) inferredPages.add(1)

  const nearEndPages = [totalPage, totalPage - 1, totalPage - 2].filter((p) => p > 0)
  if (nearEndPages.some((page) => uniquePages.has(page))) inferredPages.add(totalPage)

  for (const page of pageNumbers) {
    inferredPages.add(page)
    if (page === 1 || page === totalPage) continue
    if (page > 1) inferredPages.add(page - 1)
    if (page < totalPage) inferredPages.add(page + 1)
  }

  const sortedRecorded = [...uniquePages].sort((a, b) => a - b)
  for (let i = 0; i < sortedRecorded.length - 1; i++) {
    const currentPage = sortedRecorded[i]
    const nextPage = sortedRecorded[i + 1]
    if (!currentPage || !nextPage) continue
    const gap = nextPage - currentPage
    if (gap >= 2 && gap <= 4) {
      for (let page = currentPage + 1; page < nextPage; page++) inferredPages.add(page)
    }
  }

  return (inferredPages.size / totalPage) * 100 >= MINIMUM_PAGE_PERCENTAGE_THRESHOLD
}
